<script lang="ts">
  import { onMount } from 'svelte';
  import { select } from 'd3-selection';

  type Bar = {
    method: string;
    // When set, the label renders `methodPre` + a link on `linkText`.
    methodPre?: string;
    link?: string;
    linkText?: string;
    found: number;
    total: number;
    highlight?: boolean;
  };

  // Ordered ascending so the bars step up to the right.
  const bars: Bar[] = [
    { method: 'Frontier model with vanilla coding harness', found: 2, total: 7 },
    {
      method: 'Frontier model with vanilla coding harness with Pashov skill',
      methodPre: 'Frontier model with vanilla coding harness with ',
      link: 'https://github.com/pashov/skills/blob/main/solidity-auditor/SKILL.md',
      linkText: 'Pashov skill',
      found: 4,
      total: 7
    },
    { method: 'TestMachine Azimuth', found: 7, total: 7, highlight: true }
  ];

  let svgEl: SVGSVGElement;

  onMount(() => {
    // viewBox coordinate space — the SVG scales responsively via CSS.
    const W = 760;
    const H = 340;
    const TOP = 46; // headroom for the value labels above the tallest bar
    const max = Math.max(...bars.map((b) => b.total));
    const n = bars.length;
    const slot = W / n;
    const barW = Math.min(110, slot * 0.5);
    const baseline = H;
    const yOf = (v: number) => TOP + (H - TOP) * (1 - v / max);
    const cx = (i: number) => slot * (i + 0.5);

    const svg = select(svgEl).attr('viewBox', `0 0 ${W} ${H}`);
    svg.selectAll('*').remove();

    // Ground line the bars sit on.
    svg
      .append('line')
      .attr('class', 'd3-baseline')
      .attr('x1', 0)
      .attr('x2', W)
      .attr('y1', baseline)
      .attr('y2', baseline);

    const g = svg.selectAll('g.bar').data(bars).join('g').attr('class', 'bar');

    g.append('rect')
      .attr('class', (d) => (d.highlight ? 'd3-bar d3-bar--hl' : 'd3-bar'))
      .attr('x', (_d, i) => cx(i) - barW / 2)
      .attr('width', barW)
      .attr('y', (d) => yOf(d.found))
      .attr('height', (d) => baseline - yOf(d.found));

    const vals = g
      .append('text')
      .attr('class', (d) => (d.highlight ? 'd3-val d3-val--hl' : 'd3-val'))
      .attr('x', (_d, i) => cx(i))
      .attr('y', (d) => yOf(d.found) - 14)
      .attr('text-anchor', 'middle');
    vals.append('tspan').text((d) => d.found);
    vals.append('tspan').attr('class', 'd3-val-total').text((d) => ` / ${d.total}`);
  });
</script>

<section class="benchmark" id="benchmark">
  <div class="container">
    <div class="chart">
      <svg
        bind:this={svgEl}
        class="chart-svg"
        role="img"
        aria-label="Vulnerabilities found out of 7: frontier model with vanilla coding harness 2, frontier model with vanilla coding harness with Pashov skill 4, TestMachine Azimuth 7"
      ></svg>
      <div class="chart-labels">
        {#each bars as bar}
          <div class="chart-label" class:highlight={bar.highlight}>
            {#if bar.link}
              {bar.methodPre}<a
                class="pashov-link"
                href={bar.link}
                target="_blank"
                rel="noopener noreferrer">{bar.linkText}</a
              >
            {:else}
              {bar.method}
            {/if}
          </div>
        {/each}
      </div>
      <p class="chart-caption">
        Number of vulnerabilities identified in an
        <a
          class="evmbench-link"
          href="https://openai.com/index/introducing-evmbench/"
          target="_blank"
          rel="noopener noreferrer">EVMBench</a
        > audit
      </p>
      <p class="chart-caption chart-caption--sub">
        See Azimuth's full EVMBench results on <a class="caption-link" href="/evmbench">our leaderboard</a>
      </p>
    </div>
  </div>
</section>

<style>
  .benchmark {
    /* Sits under the shared headline; the section below owns the gap, so no
       vertical padding here. */
    padding: 0;
    border-bottom: none;
  }

  /* ── Bar chart (rendered by d3 into .chart-svg) ────────────────────────── */
  .chart {
    max-width: 760px;
    margin: 0 auto;
  }
  .chart-svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }

  .chart :global(.d3-baseline) {
    stroke: var(--border-subtle);
    stroke-width: 1;
  }
  .chart :global(.d3-bar) {
    fill: var(--fg-muted);
  }
  .chart :global(.d3-bar--hl) {
    fill: var(--accent);
  }
  .chart :global(.d3-val) {
    font-family: var(--font-display);
    font-size: 22px;
    font-weight: 700;
    fill: var(--fg);
  }
  .chart :global(.d3-val-total) {
    fill: var(--fg-dim);
    font-weight: 600;
  }
  .chart :global(.d3-val--hl),
  .chart :global(.d3-val--hl .d3-val-total) {
    fill: var(--accent);
  }

  /* Category labels — HTML so the Pashov skill link is a real anchor. They use
     a 3-column grid whose centers line up with the bars' band centers. */
  .chart-labels {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-top: 18px;
  }
  .chart-label {
    font-family: var(--font-body);
    font-size: 0.8125rem;
    line-height: 1.4;
    color: var(--fg-muted);
    text-align: center;
    padding: 0 8px;
  }
  .chart-label.highlight {
    color: var(--fg);
    font-weight: 600;
  }
  .chart-caption {
    margin: 28px 0 0;
    text-align: center;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    color: var(--fg);
  }
  .chart-caption--sub {
    margin-top: 6px;
  }
  .evmbench-link,
  .caption-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }
  .evmbench-link:hover,
  .caption-link:hover {
    color: var(--accent);
  }
  .pashov-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }
  .pashov-link:hover {
    color: var(--fg);
  }

  @media (max-width: 560px) {
    .chart-label {
      font-size: 0.75rem;
      padding: 0 4px;
    }
  }
</style>
